import { Action, createAction, handleActions } from "redux-actions";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import {
  GetDatosPagoResponse,
  GetSolicitudEmisionResponse,
  PatchRechazarSolicitudRequest,
  PatchSolicitudEmisionRequest,
} from "~api/types";
import {
  CampoFormulario,
  Dispatch,
  FechaFormulario,
  GetState,
  Services,
} from "../types";

interface DatosPagoState {
  datosPago: string;
  fechaVencimientoTarjetaCredito: string;
}

/**
 * `detalleSolicitudEmision` queda en null hasta que responde el backend; la
 * pantalla no lo lee mientras `isLoading` sea true.
 */
interface DetalleSolicitudPendienteSucursalState {
  detalleSolicitudEmision: GetSolicitudEmisionResponse | null;
  isLoading: boolean;
  polizaCreada: boolean;
  creatingPoliza: boolean;
  datosPago: DatosPagoState | null;
  emailSelected: CampoFormulario<string>;
  dominioSelected: CampoFormulario<string>;
  vigenciaDesdeSelected: CampoFormulario<FechaFormulario>;
  nroMotorSelected: CampoFormulario<string>;
  nroChasisSelected: CampoFormulario<string>;
  submitting: boolean;
  loadingError: boolean;
  errorMessage?: string;
  solicitudRechazada?: boolean;
  fotoFrontalFileNameSelected: CampoFormulario<string | null>;
  fotoTraseraFileNameSelected: CampoFormulario<string | null>;
  fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>;
  fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>;
}

const getDetalleSolicitudEmisionInited = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_INITED"
);
const getDetalleSolicitudEmisionCompleted = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_COMPLETED",
  (detalleSolicitudEmision: GetSolicitudEmisionResponse) => ({
    detalleSolicitudEmision,
  })
);
const getDetalleSolicitudEmisionFailed = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getDetalleSolicitudEmision = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getDetalleSolicitudEmisionInited());

      const response = await services.api
        .solicitudesEmision()
        .getDetalleSolicitudEmision(id);

      dispatch(getDetalleSolicitudEmisionCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getDetalleSolicitudEmisionFailed(error));
    }
  };
};

const patchSolicitudEmisionInited = createAction(
  "UPDATE_SOLICITUD_EMISION_INITED"
);
const patchSolicitudEmisionCompleted = createAction(
  "UPDATE_SOLICITUD_EMISION_COMPLETED",
  (response: Response) => undefined
);
const patchSolicitudEmisionFailed = createAction(
  "UPDATE_SOLICITUD_EMISION_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchSolicitudEmision = (
  solicitudEmisionId: string,
  data: PatchSolicitudEmisionRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(patchSolicitudEmisionInited());

      const response = await services.api
        .solicitudesEmision()
        .patchSolicitudEmision(solicitudEmisionId, data);

      dispatch(patchSolicitudEmisionCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(patchSolicitudEmisionFailed(error));
      dispatch(
        queueMessage("Ocurrió un error al crear la póliza. Intente nuevamente.")
      );
    }
  };
};

const getDatosPagoInited = createAction("OBTENER_DATOS_PAGO_INITED");
const getDatosPagoCompleted = createAction(
  "OBTENER_DATOS_PAGO_COMPLETED",
  (response: GetDatosPagoResponse | null | undefined) => {
    let datosPago = "";
    let fechaVencimientoTarjetaCredito = "";

    if (response != null) {
      datosPago = response.datoDePago;
      fechaVencimientoTarjetaCredito = response.fechaVencimientoTarjetaCredito;
    }

    return { datosPago, fechaVencimientoTarjetaCredito };
  }
);
const getDatosPagoFailed = createAction(
  "OBTENER_DATOS_PAGO_FAILED",
  (error: unknown) => ({
    error,
  })
);

const setEmail = createAction(
  "SET_EMAIL",
  (emailSelected: CampoFormulario<string>) => ({
    emailSelected,
  })
);
const setDominio = createAction(
  "SET_DOMINIO",
  (dominioSelected: CampoFormulario<string>) => ({
    dominioSelected,
  })
);
const setVigenciaDesde = createAction(
  "SET_VIGENCIA_DESDE",
  (vigenciaDesdeSelected: CampoFormulario<FechaFormulario>) => ({
    vigenciaDesdeSelected,
  })
);
const setNroMotor = createAction(
  "SET_NRO_MOTOR",
  (nroMotorSelected: CampoFormulario<string>) => ({
    nroMotorSelected,
  })
);
const setNroChasis = createAction(
  "SET_NRO_CHASIS",
  (nroChasisSelected: CampoFormulario<string>) => ({
    nroChasisSelected,
  })
);

const getDatosPago = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getDatosPagoInited());

      const response = await services.api.solicitudesEmision().getDatosPago(id);

      dispatch(getDatosPagoCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getDatosPagoFailed(error));
    }
  };
};

const patchRechazarSolicitudInited = createAction("RECHAZAR_SOLICITUD_INITED");
const patchRechazarSolicitudCompleted = createAction(
  "RECHAZAR_SOLICITUD_COMPLETED",
  (response: Response) => undefined
);
const patchRechazarSolicitudFailed = createAction(
  "RECHAZAR_SOLICITUD_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchRechazarSolicitud = (
  solicitudEmisionId: string,
  data: PatchRechazarSolicitudRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(patchRechazarSolicitudInited());

      const response = await services.api
        .solicitudesEmision()
        .patchRechazarSolicitud(solicitudEmisionId, data);

      dispatch(patchRechazarSolicitudCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(patchRechazarSolicitudFailed(error));
      dispatch(
        queueMessage(
          "Ocurrió un error al rechazar la solicitud. Intente nuevamente."
        )
      );
    }
  };
};

const clearDatosPago = createAction(
  "CLEAR_DATOS_PAGO",
  (datosPago?: unknown) => ({
    datosPago,
  })
);

const clearData = createAction("CLEAR_DATA", () => ({}));

const setSubmitting = createAction("SET_SUBMITTING", (submitting: boolean) => ({
  submitting,
}));

const setFotoFrontalFileName = createAction(
  "SET_FOTO_FRONTAL_FILENAME",
  (fotoFrontalFileNameSelected: CampoFormulario<string | null>) => ({
    fotoFrontalFileNameSelected,
  })
);

const setFotoTraseraFileName = createAction(
  "SET_FOTO_TRASERA_FILENAME",
  (fotoTraseraFileNameSelected: CampoFormulario<string | null>) => ({
    fotoTraseraFileNameSelected,
  })
);

const setFotoLateralIzquierdoFileName = createAction(
  "SET_FOTO_LATERAL_IZQUIERDO_FILENAME",
  (fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>) => ({
    fotoLateralIzquierdoFileNameSelected,
  })
);

const setFotoLateralDerechoFileName = createAction(
  "SET_FOTO_LATERAL_DERECHO_FILENAME",
  (fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>) => ({
    fotoLateralDerechoFileNameSelected,
  })
);

const detalleSolicitudEmisionInitialState: DetalleSolicitudPendienteSucursalState =
  {
    detalleSolicitudEmision: null,
    isLoading: true,
    polizaCreada: false,
    creatingPoliza: false,
    datosPago: null,
    emailSelected: { value: "", valid: false, focus: false },
    dominioSelected: { value: "", valid: false, focus: false },
    vigenciaDesdeSelected: { value: new Date(), valid: true, focus: false },
    nroMotorSelected: { value: "", valid: false, focus: false },
    nroChasisSelected: { value: "", valid: false, focus: false },
    submitting: false,
    loadingError: false,
    fotoFrontalFileNameSelected: { value: null, valid: false, focus: false },
    fotoTraseraFileNameSelected: { value: null, valid: false, focus: false },
    fotoLateralIzquierdoFileNameSelected: {
      value: null,
      valid: false,
      focus: false,
    },
    fotoLateralDerechoFileNameSelected: {
      value: null,
      valid: false,
      focus: false,
    },
  };

type State = DetalleSolicitudPendienteSucursalState;

const detalleSolicitudPendienteSucursalReducer = handleActions<State, any>(
  {
    [getDetalleSolicitudEmisionInited.toString()]: (): State => {
      return {
        ...detalleSolicitudEmisionInitialState,
      };
    },
    [getDetalleSolicitudEmisionCompleted.toString()]: (
      state: State,
      action: Action<{ detalleSolicitudEmision: GetSolicitudEmisionResponse }>
    ): State => {
      return {
        ...state,
        detalleSolicitudEmision: action.payload.detalleSolicitudEmision,
        isLoading: false,
      };
    },
    [getDetalleSolicitudEmisionFailed.toString()]: (
      state: State,
      action: Action<{ error: any }>
    ): State => {
      return {
        ...state,
        loadingError: true,
        errorMessage: action.payload.error.responseStatus.message,
      };
    },
    [patchSolicitudEmisionInited.toString()]: (state: State): State => {
      return {
        ...state,
        creatingPoliza: true,
        isLoading: true,
      };
    },
    [patchSolicitudEmisionCompleted.toString()]: (state: State): State => {
      return {
        ...state,
        polizaCreada: true,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [patchSolicitudEmisionFailed.toString()]: (state: State): State => {
      return {
        ...state,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [patchRechazarSolicitudInited.toString()]: (state: State): State => {
      return {
        ...state,
      };
    },
    [patchRechazarSolicitudCompleted.toString()]: (state: State): State => {
      return {
        ...state,
        solicitudRechazada: true,
      };
    },
    [patchRechazarSolicitudFailed.toString()]: (state: State): State => {
      return {
        ...state,
      };
    },
    [getDatosPagoInited.toString()]: (state: State): State => {
      return {
        ...state,
      };
    },
    [getDatosPagoCompleted.toString()]: (
      state: State,
      action: Action<DatosPagoState>
    ): State => {
      let datosPago = action.payload.datosPago;
      let fechaVencimientoTarjetaCredito =
        action.payload.fechaVencimientoTarjetaCredito;
      return {
        ...state,
        datosPago: { datosPago, fechaVencimientoTarjetaCredito },
      };
    },
    [getDatosPagoFailed.toString()]: (state: State): State => {
      return {
        ...state,
      };
    },
    [clearDatosPago.toString()]: (state: State): State => {
      return {
        ...state,
        datosPago: null,
      };
    },
    [clearData.toString()]: (): State => {
      return {
        ...detalleSolicitudEmisionInitialState,
      };
    },
    [setEmail.toString()]: (
      state: State,
      action: Action<{ emailSelected: CampoFormulario<string> }>
    ): State => {
      return {
        ...state,
        emailSelected: action.payload.emailSelected,
      };
    },
    [setDominio.toString()]: (
      state: State,
      action: Action<{ dominioSelected: CampoFormulario<string> }>
    ): State => {
      return {
        ...state,
        dominioSelected: action.payload.dominioSelected,
      };
    },
    [setVigenciaDesde.toString()]: (
      state: State,
      action: Action<{
        vigenciaDesdeSelected: CampoFormulario<FechaFormulario>;
      }>
    ): State => {
      return {
        ...state,
        vigenciaDesdeSelected: action.payload.vigenciaDesdeSelected,
      };
    },
    [setNroMotor.toString()]: (
      state: State,
      action: Action<{ nroMotorSelected: CampoFormulario<string> }>
    ): State => {
      return {
        ...state,
        nroMotorSelected: action.payload.nroMotorSelected,
      };
    },
    [setNroChasis.toString()]: (
      state: State,
      action: Action<{ nroChasisSelected: CampoFormulario<string> }>
    ): State => {
      return {
        ...state,
        nroChasisSelected: action.payload.nroChasisSelected,
      };
    },
    [setSubmitting.toString()]: (
      state: State,
      action: Action<{ submitting: boolean }>
    ): State => {
      return {
        ...state,
        submitting: action.payload.submitting,
        emailSelected: { ...state.emailSelected, focus: false },
        dominioSelected: { ...state.dominioSelected, focus: false },
        nroMotorSelected: { ...state.nroMotorSelected, focus: false },
        nroChasisSelected: { ...state.nroChasisSelected, focus: false },
        vigenciaDesdeSelected: { ...state.vigenciaDesdeSelected, focus: false },
      };
    },
    [setFotoFrontalFileName.toString()]: (
      state: State,
      action: Action<{
        fotoFrontalFileNameSelected: CampoFormulario<string | null>;
      }>
    ): State => {
      return {
        ...state,
        fotoFrontalFileNameSelected: action.payload.fotoFrontalFileNameSelected,
      };
    },
    [setFotoTraseraFileName.toString()]: (
      state: State,
      action: Action<{
        fotoTraseraFileNameSelected: CampoFormulario<string | null>;
      }>
    ): State => {
      return {
        ...state,
        fotoTraseraFileNameSelected: action.payload.fotoTraseraFileNameSelected,
      };
    },
    [setFotoLateralIzquierdoFileName.toString()]: (
      state: State,
      action: Action<{
        fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>;
      }>
    ): State => {
      return {
        ...state,
        fotoLateralIzquierdoFileNameSelected:
          action.payload.fotoLateralIzquierdoFileNameSelected,
      };
    },
    [setFotoLateralDerechoFileName.toString()]: (
      state: State,
      action: Action<{
        fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>;
      }>
    ): State => {
      return {
        ...state,
        fotoLateralDerechoFileNameSelected:
          action.payload.fotoLateralDerechoFileNameSelected,
      };
    },
  },
  detalleSolicitudEmisionInitialState
);

export {
  detalleSolicitudPendienteSucursalReducer,
  getDetalleSolicitudEmision,
  patchSolicitudEmision,
  patchRechazarSolicitud,
  getDatosPago,
  clearDatosPago,
  setEmail,
  setDominio,
  setNroChasis,
  setNroMotor,
  setVigenciaDesde,
  clearData,
  setSubmitting,
  setFotoFrontalFileName,
  setFotoTraseraFileName,
  setFotoLateralDerechoFileName,
  setFotoLateralIzquierdoFileName,
};
